import React, {
    type ContextType,
    type FC,
    type PropsWithChildren,
    use,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import { CompanyContext } from "@domain/context/CompanyContext";
import { GQL } from "@binale-tech/shared";

import { type DataSnapshot, child } from "firebase/database";

import { getDmsDocumentTypes } from "@domain/context/queries/ctxQueries.graphql";

import { refDocuments } from "@domain/api/firebase/firebaseRootRefs";
import { useLazyQuery } from "@apollo/client/react";

import { DmsTypeOptions } from "@dms/configs/constants";
import { type IDocumentType } from "@domain/models/dms";
import { DmsDataContext, DmsDocumentsContext, DmsTypesContext } from "@domain/context/DmsDataContexts";
import { DmsUserProvider } from "@dms/scripts/context/DmsUserProvider";
import { useCollectionListener } from "@domain/context/hooks/useCollectionListener";
import { useDmsAssociationsData } from "@domain/context/hooks/useDmsAssociationsData";
import { ProductAccessUtils } from "@domain/models/utils/ProductAccessUtils";
import { logDatasetSize } from "@domain/utils/datasetSizeLogger";
import { ignoreAbort } from "@domain/graphql/abortErrors";

const getTypesCacheKey = (companyId: string) => {
    return `DMS_TYPES_CACHE:${companyId}`;
};

export const DmsDataProvider: FC<PropsWithChildren> = ({ children }) => {
    const { companyGQL } = use(CompanyContext);

    const companyId = companyGQL?.id;
    const [documentTypes, setDocumentTypes] = useState<IDocumentType[]>(
        JSON.parse(localStorage.getItem(getTypesCacheKey(companyId)) ?? "[]"),
    );

    const [fetchDmsDocumentTypes] = useLazyQuery(getDmsDocumentTypes, { fetchPolicy: "no-cache" });

    const shouldSkipLoad = useMemo(
        () => !ProductAccessUtils.hasCompanyProductAccess(GQL.IProductKey.Dms, companyGQL),
        [companyGQL],
    );
    const ref = useMemo(() => child(refDocuments, String(companyId)), [companyId]);
    const initializer = useCallback((snap: DataSnapshot) => snap.val(), []);

    const { state: documentsMap, list: documents } = useCollectionListener(ref, initializer, shouldSkipLoad);

    const dmsAssociationsData = useDmsAssociationsData();

    useEffect(() => {
        if (documentsMap.size > 0) {
            logDatasetSize("DmsDataProvider.documents", { type: "map", map: documentsMap });
        }
    }, [documentsMap]);

    useEffect(() => {
        if (documentTypes.length > 0) {
            logDatasetSize("DmsDataProvider.documentTypes", { type: "list", list: documentTypes });
        }
    }, [documentTypes]);

    useEffect(() => {
        const { documentAccountingRecords, documentBankingTransactions, documentCashRegisterTransactions } =
            dmsAssociationsData;
        if (documentAccountingRecords.size > 0) {
            logDatasetSize("DmsDataProvider.documentAccountingRecords", {
                type: "map",
                map: documentAccountingRecords,
            });
        }
        if (documentBankingTransactions.size > 0) {
            logDatasetSize("DmsDataProvider.documentBankingTransactions", {
                type: "map",
                map: documentBankingTransactions,
            });
        }
        if (documentCashRegisterTransactions.size > 0) {
            logDatasetSize("DmsDataProvider.documentCashRegisterTransactions", {
                type: "map",
                map: documentCashRegisterTransactions,
            });
        }
    }, [dmsAssociationsData]);

    const fetchTypes = useCallback(async () => {
        const res = await fetchDmsDocumentTypes({
            variables: { companyId },
        });

        const types = DmsTypeOptions.filter((v) => Boolean(v.value)).map(({ value }) => {
            const dbDmsType = res.data.dmsTypes.find((v) => value === v.id);
            if (!dbDmsType) {
                throw new Error(`Unable to find type ${value} in the backend data`);
            }
            return dbDmsType as IDocumentType;
        });

        setDocumentTypes((prevTypes) => {
            if (JSON.stringify(prevTypes) === JSON.stringify(types)) {
                return prevTypes;
            }
            localStorage.setItem(getTypesCacheKey(companyId), JSON.stringify(types));
            return types;
        });
    }, [fetchDmsDocumentTypes, companyId]);

    useEffect(() => {
        if (!companyId) {
            return undefined;
        }
        let cancelled = false;
        const onVisibilityChange = () => {
            if (cancelled || document.visibilityState !== "visible") {
                return;
            }
            fetchTypes().catch(ignoreAbort);
        };

        queueMicrotask(() => fetchTypes().catch(ignoreAbort));
        document.addEventListener("visibilitychange", onVisibilityChange);

        return () => {
            cancelled = true;
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, [companyId, fetchTypes]);

    const documentsValue = useMemo<ContextType<typeof DmsDocumentsContext>>(
        () => ({ documents, documentsMap }),
        [documents, documentsMap],
    );

    const dataValue = useMemo<ContextType<typeof DmsDataContext>>(
        () => ({ dmsAssociationsData }),
        [dmsAssociationsData],
    );

    const typesValue = useMemo<ContextType<typeof DmsTypesContext>>(
        () => ({ documentTypes, fetchTypes }),
        [documentTypes, fetchTypes],
    );

    return (
        <DmsDocumentsContext value={documentsValue}>
            <DmsTypesContext value={typesValue}>
                <DmsDataContext value={dataValue}>
                    <DmsUserProvider>{children}</DmsUserProvider>
                </DmsDataContext>
            </DmsTypesContext>
        </DmsDocumentsContext>
    );
};
